import type { Backlog, IssueCard } from '@tasks/contracts';

/** Чистые операции модели бэклога (тестируются без DOM). */

export function totalPoints(issues: readonly IssueCard[]): number {
  return issues.reduce((sum, issue) => sum + (issue.storyPoints ?? 0), 0);
}

/**
 * «Выполнено» в бэклоге — по подзадачам карточки: у `IssueCard` нет категории
 * статуса, поэтому опираемся на прогресс подзадач, как это делает API
 * (`doneCount` в группе).
 */
export function isCompletedCard(issue: IssueCard): boolean {
  return issue.subtasks.total > 0 && issue.subtasks.done === issue.subtasks.total;
}

export function doneCount(issues: readonly IssueCard[]): number {
  return issues.filter(isCompletedCard).length;
}

/** Группировка задач бэклога по эпикам (UC-SPR-01 AC2). */
export function groupByEpic(issues: readonly IssueCard[]): readonly {
  readonly epic: IssueCard['epic'];
  readonly issues: readonly IssueCard[];
  readonly totalPoints: number;
  readonly doneCount: number;
}[] {
  const groups = new Map<string, { epic: IssueCard['epic']; issues: IssueCard[] }>();
  for (const issue of issues) {
    const groupKey = issue.epic?.id ?? '__none__';
    const existing = groups.get(groupKey) ?? { epic: issue.epic, issues: [] };
    existing.issues.push(issue);
    groups.set(groupKey, existing);
  }
  return [...groups.values()].map((group) => ({
    epic: group.epic,
    issues: group.issues,
    totalPoints: totalPoints(group.issues),
    doneCount: doneCount(group.issues),
  }));
}

/** Найти задачу в любом контейнере бэклога. */
export function findIssue(backlog: Backlog, issueId: string): IssueCard | null {
  for (const issue of backlog.backlog) {
    if (issue.id === issueId) {
      return issue;
    }
  }
  for (const group of backlog.sprintGroups) {
    for (const issue of group.issues) {
      if (issue.id === issueId) {
        return issue;
      }
    }
  }
  return null;
}

/** Оптимистичный перенос карточки между бэклогом и спринтом (UC-SPR-03). */
export function moveIssueBetweenGroups(
  backlog: Backlog,
  issueId: string,
  targetSprintId: string | null,
): Backlog {
  const issue = findIssue(backlog, issueId);
  if (!issue) {
    return backlog;
  }
  const next: IssueCard = { ...issue, sprintId: targetSprintId };

  const stripped: Backlog = {
    ...backlog,
    backlog: backlog.backlog.filter((item) => item.id !== issueId),
    sprintGroups: backlog.sprintGroups.map((group) => {
      const issues = group.issues.filter((item) => item.id !== issueId);
      return {
        ...group,
        issues,
        totalPoints: totalPoints(issues),
        doneCount: doneCount(issues),
      };
    }),
  };

  if (targetSprintId === null) {
    return { ...stripped, backlog: [next, ...stripped.backlog] };
  }

  if (!stripped.sprintGroups.some((group) => group.sprint.id === targetSprintId)) {
    return backlog;
  }

  return {
    ...stripped,
    sprintGroups: stripped.sprintGroups.map((group) => {
      if (group.sprint.id !== targetSprintId) {
        return group;
      }
      const issues = [next, ...group.issues];
      return { ...group, issues, totalPoints: totalPoints(issues), doneCount: doneCount(issues) };
    }),
  };
}

/** Дата окончания не раньше даты начала (UC-SPR-02 AC1). */
export function isValidSprintRange(startDate: string | null, endDate: string | null): boolean {
  if (!startDate || !endDate) {
    return true;
  }
  return endDate >= startDate;
}

export function assertSprintRange(startDate: string | null, endDate: string | null): void {
  if (!isValidSprintRange(startDate, endDate)) {
    throw new Error('Дата окончания не может быть раньше даты начала');
  }
}

export function collectIssues(backlog: Backlog | null): readonly IssueCard[] {
  if (!backlog) {
    return [];
  }
  return [...backlog.backlog, ...backlog.sprintGroups.flatMap((group) => group.issues)];
}

export function collectIds(backlog: Backlog | null): readonly string[] {
  return collectIssues(backlog).map((issue) => issue.id);
}
